import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LegalContentService, LegalFileMeta } from './legal-content.service';

@Component({
  selector: 'landing-legal-article',
  templateUrl: './legal-article.component.html',
  styleUrls: ['./legal-article.component.scss'],
})
export class LegalArticleComponent implements OnInit {
  sectionKey: string = '';
  title: string = '';
  files: LegalFileMeta[] = [];
  selected?: LegalFileMeta;
  contentSafeHtml?: SafeHtml;
  loading = false;
  error?: string;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private contentService: LegalContentService,
    private sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    const data = this.route.snapshot.data;
    this.sectionKey = data?.['key'] || this.route.snapshot.paramMap.get('section') || '';
    if (!this.sectionKey) return;

    const section = this.contentService.getSection(this.sectionKey);
    if (!section) return;

    this.title = section.title;
    this.files = section.files || [];

    // reorder files so German appears first, English second, others afterwards
    this.reorderFiles();

    // react to param changes: when URL contains a file-slug, load that file
    this.route.paramMap.subscribe(params => {
      const paramFile = params.get('file');

      if (!paramFile) {
        // No explicit file in URL — choose default.
        if (this.files.length) {
          // Prefer German file if available (lang === 'GER' or 'DE' or filename contains '_ger_')
          const germanFile = this.files.find(f =>
            (f.lang || '').toLowerCase() === 'ger' ||
            (f.lang || '').toLowerCase() === 'de' ||
            (f.filename || '').toLowerCase().includes('_ger_')
          );
          const defaultFile = germanFile || this.files[0];

          const url = this.contentService.buildShareUrl(this.sectionKey, defaultFile);
          // Only navigate if current url doesn't already end with the slug (defensive)
          const currentPath = (typeof window !== 'undefined' && window.location) ? window.location.pathname + window.location.search + window.location.hash : '';
          if (!currentPath.endsWith('/' + this.contentService.fileToSlug(defaultFile))) {
            this.router.navigateByUrl(url, { replaceUrl: true });
            return; // navigation will re-trigger paramMap and load file
          }
        }

        // If no files or navigation not needed, ensure we load the first file once (or german chosen above)
        if (this.files.length && !this.selected) {
          // prefer german if available when directly loading without navigation
          const germanFile = this.files.find(f =>
            (f.lang || '').toLowerCase() === 'ger' ||
            (f.lang || '').toLowerCase() === 'de' ||
            (f.filename || '').toLowerCase().includes('_ger_')
          );
          const defaultFile = germanFile || this.files[0];
          this.selectFile(defaultFile);
        }
        return;
      }

      // If there is a file param, resolve it and load the corresponding file
      const matched = this.contentService.getFileBySlug(this.sectionKey, paramFile);
      if (matched) {
        if (!this.selected || this.selected.filename !== matched.filename) {
          this.selectFile(matched);
        }
        return;
      }

      // fallback to first file if param didn't match
      if (this.files.length && !this.selected) {
        const germanFile = this.files.find(f =>
          (f.lang || '').toLowerCase() === 'ger' ||
          (f.lang || '').toLowerCase() === 'de' ||
          (f.filename || '').toLowerCase().includes('_ger_')
        );
        const defaultFile = germanFile || this.files[0];
        this.selectFile(defaultFile);
      }
    });
  }

  // called when user clicks a language/version button:
  // navigate to the canonical absolute share URL so we don't append segments repeatedly
  onSelectFile(file: LegalFileMeta) {
    const url = this.contentService.buildShareUrl(this.sectionKey, file);
    // navigate absolute to the canonical URL (e.g. /legal/terms-and-conditions/terms_eng_v1_28-09-2025)
    this.router.navigateByUrl(url);
  }

  selectFile(file: LegalFileMeta) {
    this.selected = file;
    this.loading = true;
    this.error = undefined;
    this.contentService.fetchFileContent(this.sectionKey, file.filename).subscribe({
      next: txt => {
        this.contentSafeHtml = this.sanitizer.bypassSecurityTrustHtml(txt);
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load document';
        this.contentSafeHtml = undefined;
        this.loading = false;
      }
    });
  }

  // return the relative share url for the currently passed file
  getShareUrl(file: LegalFileMeta): string {
    return this.contentService.buildShareUrl(this.sectionKey, file);
  }

  // copy the full share URL (origin + relative) to clipboard or fallback to prompt
  copyShareUrl(file: LegalFileMeta) {
    const relative = this.getShareUrl(file);
    const full = (typeof window !== 'undefined' && window.location ? `${window.location.origin}${relative}` : relative);

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(full).catch(() => {
        // fallback
        window.prompt('Copy the link below', full);
      });
    } else {
      // older browsers: show prompt for manual copy
      window.prompt('Copy the link below', full);
    }
  }

  // move GER first, ENG second, keep original relative order for remaining files
  private reorderFiles() {
    if (!this.files || this.files.length <= 1) return;

    const isGer = (f: LegalFileMeta) => ((f.lang || '').toLowerCase() === 'ger') || ((f.lang || '').toLowerCase() === 'de') || (f.filename || '').toLowerCase().includes('_ger_');
    const isEng = (f: LegalFileMeta) => ((f.lang || '').toLowerCase() === 'eng') || ((f.lang || '').toLowerCase() === 'en') || (f.filename || '').toLowerCase().includes('_eng_');

    const gerFiles: LegalFileMeta[] = [];
    const engFiles: LegalFileMeta[] = [];
    const others: LegalFileMeta[] = [];

    this.files.forEach(f => {
      if (isGer(f)) gerFiles.push(f);
      else if (isEng(f)) engFiles.push(f);
      else others.push(f);
    });

    // new order: single german (or all german first), then single english, then others
    this.files = [...gerFiles, ...engFiles, ...others];
  }
}
